import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Card, CardContent } from 'src/components/ui/card';
import { ArrowUUpLeftIcon, WarningIcon } from '@phosphor-icons/react';
import type { InterviewPhase, SopStepView } from '@erp/rpc';
import { T, useGT } from 'gt-react';
import { PhaseDivider } from './PhaseDivider';

interface SopDraftViewerProps {
  steps: SopStepView[];
  /** Called to retract a provisional step (sends a correction to the interview). */
  onRetract?: (step: SopStepView) => void;
  /** Disables the retract affordance while a response is in flight. */
  retractDisabled?: boolean;
}

/**
 * Renders the live SOP draft an `operator.author_sop` interview is building
 * (`OperatorActivity.sopDraft.steps`). Provisional steps are dimmed and carry a
 * retract affordance; confirmed steps are solid. The codeKind marker shows how
 * the step will replay: ⚡ref (durable selector), ⚡canvas (coordinate), or
 * "agentic" (no captured code).
 *
 * Steps carry a lifecycle `phase` (setup / main / teardown). When more than one phase is present
 * the draft is shown as labeled groups — setup (runs once, e.g. login) is visibly separate from
 * the repeatable actions, and teardown last. A single-phase (all-`main`) draft — the common case —
 * renders as one flat list, unchanged.
 */

function codeKindMarker(step: SopStepView): {
  label: string;
  symbol: string;
} {
  if (step.codeKind === 'ref') return { label: 'ref', symbol: '⚡' };
  if (step.codeKind === 'coordinate') return { label: 'canvas', symbol: '⚡' };
  return { label: 'agentic', symbol: '' };
}

/** Group order + labels. Setup first (the once-per-run preamble), then the repeatable actions,
 * then teardown. */
const PHASE_ORDER: InterviewPhase[] = ['setup', 'main', 'teardown'];

function StepCard({
  step,
  onRetract,
  retractDisabled,
}: {
  step: SopStepView;
  onRetract?: (step: SopStepView) => void;
  retractDisabled: boolean;
}) {
  const gt = useGT();
  const isProvisional = step.status === 'provisional';
  const marker = codeKindMarker(step);
  return (
    <Card className={isProvisional ? 'opacity-70 border-dashed' : undefined}>
      <CardContent className="flex items-start gap-2 p-3">
        <span className="text-xs font-mono text-muted-foreground pt-0.5 w-6 shrink-0">
          {step.index}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{step.name}</span>
            <Badge
              variant={isProvisional ? 'outline' : 'secondary'}
              className="text-xs"
            >
              {isProvisional ? (
                <T context="procedure draft step provisional status badge">
                  provisional
                </T>
              ) : (
                <T context="procedure draft step confirmed status badge">
                  confirmed
                </T>
              )}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {marker.symbol}
              {marker.label}
            </Badge>
            {step.risk === 'high' && (
              <Badge variant="destructive" className="text-xs">
                <T context="procedure draft step high-risk badge">high risk</T>
              </Badge>
            )}
            {step.health === 'fragile' && (
              <Badge
                variant="outline"
                className="border-amber-400 text-xs text-amber-700"
                title={gt(
                  'This step was captured by position or a low-confidence selector — it may break if the page changes.',
                )}
              >
                <WarningIcon className="mr-1 h-3 w-3" weight="fill" />
                <T context="procedure draft step fragile-selector warning badge">
                  may drift
                </T>
              </Badge>
            )}
          </div>
          {step.intent && (
            <p className="text-xs text-muted-foreground mt-1">{step.intent}</p>
          )}
        </div>
        {isProvisional && onRetract && (
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 h-7 w-7"
            disabled={retractDisabled}
            onClick={() => onRetract(step)}
            aria-label={gt('Retract step')}
            title={gt('Retract step')}
          >
            <ArrowUUpLeftIcon className="h-4 w-4" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export function SopDraftViewer({
  steps,
  onRetract,
  retractDisabled = false,
}: SopDraftViewerProps) {
  if (steps.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          <T context="Empty state for the live procedure draft before any steps">
            No steps captured yet. As the agent acts, steps will appear here.
          </T>
        </CardContent>
      </Card>
    );
  }

  // Older payloads (and the common single-phase session) leave every step at "main"; render those
  // as one flat list with no group chrome. Group only once a second phase actually appears.
  const usesPhases = steps.some((s) => s.phase !== 'main');
  if (!usesPhases) {
    return (
      <div className="space-y-2">
        {steps.map((step) => (
          <StepCard
            key={step.index}
            step={step}
            onRetract={onRetract}
            retractDisabled={retractDisabled}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {PHASE_ORDER.map((phase) => {
        const group = steps.filter((s) => s.phase === phase);
        if (group.length === 0) return null;
        return (
          <div key={phase} className="space-y-2">
            <PhaseDivider phase={phase} />
            {group.map((step) => (
              <StepCard
                key={step.index}
                step={step}
                onRetract={onRetract}
                retractDisabled={retractDisabled}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
